/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

export type OperatingSystem = 'mac' | 'windows' | 'linux';

/**
 * `navigator.platform` comes first because it describes the machine the
 * keyboard is attached to, while the user agent and its client hints can be
 * pinned to another platform by the embedder.
 */
function getPlatformHint(): string {
  if (typeof navigator === 'undefined') {
    return '';
  }
  const navigatorWithHints = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  return (
    navigator.platform ||
    navigatorWithHints.userAgentData?.platform ||
    navigator.userAgent ||
    ''
  );
}

/** Detects the operating system the keyboard is attached to. */
export function detectOS(): OperatingSystem {
  const platform = getPlatformHint();
  if (/mac|iphone|ipad|ipod/i.test(platform)) {
    return 'mac';
  }
  if (/win/i.test(platform)) {
    return 'windows';
  }
  return 'linux';
}

/**
 * Apple platforms take `Cmd` where the rest take `Ctrl`, since `Ctrl` + click
 * is the macOS context menu gesture.
 */
export function isApplePlatform(): boolean {
  return detectOS() === 'mac';
}
